import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import ImageDropzone from "@/components/base/ImageDropzone";
import Select from "@/components/base/Select";

const LOGO_SRC =
  "https://static.readdy.ai/image/14e48b2a3119a3da93cb07955cc0990c/70345e36bb0ad42c4823216a62f4fb9a.png";

const CATEGORIES = ["مطعم", "مقهى", "محل تجاري"];

const REGIONS = [
  "الرياض",
  "مكة المكرمة",
  "المدينة المنورة",
  "المنطقة الشرقية",
  "عسير",
  "القصيم",
  "تبوك",
  "جازان",
  "حائل",
  "نجران",
  "الباحة",
  "الجوف",
  "الحدود الشمالية",
];

interface FormState {
  brandName: string;
  category: string;
  description: string;
  ownerName: string;
  phone: string;
  email: string;
  crNumber: string;
  taxId: string;
  region: string;
}

const INITIAL: FormState = {
  brandName: "",
  category: "",
  description: "",
  ownerName: "",
  phone: "",
  email: "",
  crNumber: "",
  taxId: "",
  region: "",
};

function SectionCard({
  step,
  title,
  children,
}: {
  step: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="bg-white rounded-card border border-background-200 shadow-card p-6">
      <div className="flex items-center gap-3 mb-5">
        <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-white text-sm font-bold">
          {step}
        </span>
        <h2 className="text-lg font-bold text-foreground-950">{title}</h2>
      </div>
      <div className="flex flex-col gap-5">{children}</div>
    </section>
  );
}

function FieldLabel({
  text,
  required,
}: {
  text: string;
  required?: boolean;
}) {
  return (
    <label className="block text-sm font-medium text-foreground-800 mb-2">
      {text} {required && <span className="text-red-500">*</span>}
    </label>
  );
}

export default function Register() {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [crFile, setCrFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.brandName.trim()) next.brandName = "الاسم التجاري مطلوب";
    if (!form.category) next.category = "اختر التصنيف";
    if (!form.description.trim()) next.description = "وصف المحل مطلوب";
    if (!logoFile) next.logo = "شعار المحل مطلوب";
    if (!form.ownerName.trim()) next.ownerName = "اسم المسؤول مطلوب";
    if (!/^05\d{8}$/.test(form.phone.trim()))
      next.phone = "أدخل رقم جوال سعودي صحيح بصيغة 05xxxxxxxx";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "أدخل بريدًا إلكترونيًا صحيحًا";
    if (!form.crNumber.trim()) next.crNumber = "رقم السجل التجاري مطلوب";
    if (!crFile) next.crFile = "صورة السجل التجاري مطلوبة";
    if (!form.taxId.trim()) next.taxId = "الرقم الضريبي مطلوب";
    if (!form.region) next.region = "اختر المنطقة";
    return next;
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitted(true);
  };

  const inputClass = (key: string) =>
    `w-full h-12 rounded-btn border bg-white px-4 text-sm outline-none transition-colors duration-200 focus:ring-2 focus:ring-primary-300 ${
      errors[key] ? "border-red-300" : "border-background-200"
    }`;

  if (submitted) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center p-6">
        <div className="yc-pop w-full max-w-lg bg-white rounded-card border border-background-200 shadow-card p-10 text-center">
          <div className="mx-auto w-20 h-20 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-5xl">
            <i className="ri-checkbox-circle-fill"></i>
          </div>
          <h1 className="mt-6 text-2xl font-bold text-foreground-950">
            تم إرسال طلبك بنجاح
          </h1>
          <p className="mt-3 text-sm leading-7 text-foreground-600">
            سيتم مراجعة طلب تسجيل محلك والرد عليك قريبًا عبر رسالة SMS على رقم
            الجوال المسجّل.
          </p>
          <div className="mt-6 rounded-btn bg-primary-50 px-4 py-3 text-sm font-medium text-primary-800">
            رقم الطلب: <span dir="ltr">YC-2026-0418</span>
          </div>
          <Link
            to="/login"
            className="mt-8 inline-flex h-12 w-full items-center justify-center rounded-btn bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
          >
            العودة لتسجيل الدخول
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50 py-10 px-4">
      <div className="mx-auto max-w-[720px]">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="inline-flex items-center rounded-lg bg-white px-3 py-2 border border-background-200">
            <img src={LOGO_SRC} alt="يور كاشير" className="h-10 w-auto" />
          </div>
          <h1 className="mt-5 text-2xl font-bold text-foreground-950">
            سجّل محلك وابدأ استقبال الطلبات
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            املأ البيانات التالية وسنراجع طلبك ونفعّل حسابك
          </p>
          <div className="mt-4 flex items-center gap-2 text-xs font-medium text-foreground-500">
            <span className="rounded-full bg-primary-500 px-3 py-1 text-white">
              1 تسجيل البيانات
            </span>
            <span className="w-6 h-px bg-background-300"></span>
            <span className="rounded-full bg-background-200 px-3 py-1">
              2 المراجعة
            </span>
            <span className="w-6 h-px bg-background-300"></span>
            <span className="rounded-full bg-background-200 px-3 py-1">
              3 التفعيل
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <SectionCard step="1" title="بيانات العلامة التجارية">
            <div>
              <FieldLabel text="الاسم التجاري للمحل (العلامة التجارية)" required />
              <input
                type="text"
                name="brandName"
                value={form.brandName}
                onChange={(event) => update("brandName", event.target.value)}
                placeholder="مثال: مطعم البيت السعودي"
                className={inputClass("brandName")}
              />
              {errors.brandName && (
                <p className="mt-1.5 text-xs text-red-500">{errors.brandName}</p>
              )}
            </div>

            <div>
              <FieldLabel text="التصنيف" required />
              <Select
                value={form.category}
                options={CATEGORIES.map((item) => ({ value: item, label: item }))}
                onChange={(value) => update("category", value)}
                placeholder="اختر التصنيف"
                size="lg"
                invalid={Boolean(errors.category)}
                ariaLabel="التصنيف"
              />
              {errors.category && (
                <p className="mt-1.5 text-xs text-red-500">{errors.category}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <FieldLabel text="وصف المحل" required />
                <span
                  className={`text-xs ${
                    form.description.length > 500
                      ? "text-red-500"
                      : "text-foreground-400"
                  }`}
                >
                  {form.description.length}/500
                </span>
              </div>
              <textarea
                name="description"
                rows={4}
                maxLength={500}
                value={form.description}
                onChange={(event) => update("description", event.target.value)}
                placeholder="اكتب وصفًا مختصرًا عن محلك ونوعية المنتجات التي تقدمها"
                className={`w-full rounded-btn border bg-white px-4 py-3 text-sm outline-none transition-colors duration-200 focus:ring-2 focus:ring-primary-300 resize-none ${
                  errors.description ? "border-red-300" : "border-background-200"
                }`}
              />
              {errors.description && (
                <p className="mt-1.5 text-xs text-red-500">
                  {errors.description}
                </p>
              )}
            </div>

            <ImageDropzone
              label="شعار المحل"
              required
              aspect="square"
              error={errors.logo}
              hint="صورة مربعة — تظهر في التطبيق"
              onFile={(file) => {
                setLogoFile(file);
                setErrors((prev) => ({ ...prev, logo: "" }));
              }}
            />

            <ImageDropzone
              label="صورة الغلاف"
              aspect="landscape"
              hint="صورة أفقية اختيارية لواجهة المحل"
              onFile={() => {}}
            />
          </SectionCard>

          <SectionCard step="2" title="بيانات صاحب المحل">
            <div>
              <FieldLabel text="اسم صاحب المحل / المسؤول" required />
              <input
                type="text"
                name="ownerName"
                value={form.ownerName}
                onChange={(event) => update("ownerName", event.target.value)}
                placeholder="الاسم الكامل"
                className={inputClass("ownerName")}
              />
              {errors.ownerName && (
                <p className="mt-1.5 text-xs text-red-500">{errors.ownerName}</p>
              )}
            </div>

            <div>
              <FieldLabel text="رقم الجوال" required />
              <input
                type="tel"
                name="phone"
                dir="ltr"
                value={form.phone}
                onChange={(event) =>
                  update("phone", event.target.value.replace(/[^\d]/g, ""))
                }
                placeholder="05xxxxxxxx"
                className={`${inputClass("phone")} text-left`}
              />
              {errors.phone ? (
                <p className="mt-1.5 text-xs text-red-500">{errors.phone}</p>
              ) : (
                <p className="mt-1.5 text-xs text-foreground-400">
                  سيُستخدم لتسجيل الدخول لاحقًا
                </p>
              )}
            </div>

            <div>
              <FieldLabel text="البريد الإلكتروني" required />
              <input
                type="email"
                name="email"
                dir="ltr"
                value={form.email}
                onChange={(event) => update("email", event.target.value)}
                placeholder="name@example.com"
                className={`${inputClass("email")} text-left`}
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-500">{errors.email}</p>
              )}
            </div>
          </SectionCard>

          <SectionCard step="3" title="بيانات السجل التجاري">
            <div>
              <FieldLabel text="رقم السجل التجاري" required />
              <input
                type="text"
                name="crNumber"
                dir="ltr"
                value={form.crNumber}
                onChange={(event) =>
                  update("crNumber", event.target.value.replace(/[^\d]/g, ""))
                }
                placeholder="1010xxxxxx"
                className={`${inputClass("crNumber")} text-left`}
              />
              {errors.crNumber && (
                <p className="mt-1.5 text-xs text-red-500">{errors.crNumber}</p>
              )}
            </div>

            <ImageDropzone
              label="صورة السجل التجاري"
              required
              aspect="landscape"
              accept="image/*,application/pdf"
              hint="صورة أو ملف PDF"
              error={errors.crFile}
              onFile={(file) => {
                setCrFile(file);
                setErrors((prev) => ({ ...prev, crFile: "" }));
              }}
            />

            <div>
              <FieldLabel text="الرقم الضريبي (Tax ID)" required />
              <input
                type="text"
                name="taxId"
                dir="ltr"
                value={form.taxId}
                onChange={(event) =>
                  update("taxId", event.target.value.replace(/[^\d]/g, ""))
                }
                placeholder="300xxxxxxxxxx3"
                className={`${inputClass("taxId")} text-left`}
              />
              {errors.taxId && (
                <p className="mt-1.5 text-xs text-red-500">{errors.taxId}</p>
              )}
            </div>
          </SectionCard>

          <SectionCard step="4" title="المنطقة الجغرافية">
            <div>
              <FieldLabel text="المنطقة" required />
              <Select
                value={form.region}
                options={REGIONS.map((item) => ({ value: item, label: item }))}
                onChange={(value) => update("region", value)}
                placeholder="اختر المنطقة"
                size="lg"
                invalid={Boolean(errors.region)}
                ariaLabel="المنطقة"
              />
              {errors.region ? (
                <p className="mt-1.5 text-xs text-red-500">{errors.region}</p>
              ) : (
                <p className="mt-1.5 text-xs text-foreground-400">
                  تُستخدم لتوليد رمز المحل تلقائيًا
                </p>
              )}
            </div>
          </SectionCard>

          <button
            type="submit"
            className="h-12 w-full rounded-btn bg-primary-500 text-white text-base font-semibold hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            إرسال طلب التسجيل
          </button>

          <p className="text-center text-sm text-foreground-500">
            لديك حساب بالفعل؟{" "}
            <Link
              to="/login"
              className="font-semibold text-primary-600 hover:text-primary-700"
            >
              تسجيل الدخول
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}